<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';

import { useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import AgentSelector from '../AgentSelector.vue';
import { useVuelidate } from '@vuelidate/core';

export default {
  components: {
    Button,
    Spinner,
    PageHeader,
    AgentSelector,
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
  },

  setup() {
    return { v$: useVuelidate(), router: useRouter() };
  },

  data() {
    return {
      selectedAgents: [],
      isCreating: false,
      isLoadingAgents: false,
      agentsLoadError: false,
    };
  },

  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
    }),

    teamId() {
      return this.$route.params.teamId;
    },
    headerTitle() {
      return this.$t('TEAMS_SETTINGS.ADD.TITLE', {
        teamName: this.currentTeam.name,
      });
    },
    currentTeam() {
      return this.$store.getters['teams/getTeam'](this.teamId);
    },
  },

  mounted() {
    this.loadAgents();
  },

  methods: {
    async loadAgents() {
      if (this.isLoadingAgents) return;
      this.isLoadingAgents = true;
      this.agentsLoadError = false;
      try {
        await this.$store.dispatch('agents/get', { throwOnError: true });
      } catch {
        this.agentsLoadError = true;
      } finally {
        this.isLoadingAgents = false;
      }
    },
    updateSelectedAgents(newAgentList) {
      this.v$.selectedAgents.$touch();
      this.selectedAgents = [...newAgentList];
    },
    selectAllAgents() {
      this.selectedAgents = this.agentList.map(agent => agent.id);
    },
    async addAgents() {
      if (this.isCreating || this.isLoadingAgents || this.agentsLoadError) return;
      this.v$.selectedAgents.$touch();
      if (this.v$.selectedAgents.$invalid) return;
      this.isCreating = true;
      const { teamId, selectedAgents } = this;

      try {
        await this.$store.dispatch('teamMembers/create', {
          teamId,
          agentsList: selectedAgents,
        });
        this.router.replace({
          name: 'settings_teams_finish',
          params: {
            page: 'new',
            teamId,
          },
        });
        this.$store.dispatch('teams/get');
      } catch (error) {
        // error.message vinha do store como "AxiosError: Request failed..."
        useAlert(this.$t('TEAMS_SETTINGS.AGENTS.SAVE_ERROR'));
      }
      this.isCreating = false;
    },
  },
};
</script>

<template>
  <div class="h-full w-full px-8 pt-8 col-span-6 overflow-auto">
    <form class="flex flex-col gap-4 mx-0" @submit.prevent="addAgents">
      <PageHeader
        :header-title="headerTitle"
        :header-content="$t('TEAMS_SETTINGS.ADD.DESC')"
      />

      <div class="w-full h-full">
        <div v-if="v$.selectedAgents.$error">
          <p class="error-message pb-2">
            {{ $t('TEAMS_SETTINGS.ADD.AGENT_VALIDATION_ERROR') }}
          </p>
        </div>
        <div v-if="agentsLoadError" role="alert" class="flex flex-col items-start gap-3 py-4">
          <p class="m-0 text-sm text-n-ruby-11">{{ $t('TEAMS_SETTINGS.AGENTS.LOAD_ERROR') }}</p>
          <Button type="button" class="min-h-11" :label="$t('TEAMS_SETTINGS.AGENTS.RETRY')" @click="loadAgents" />
        </div>
        <div v-else-if="isLoadingAgents" class="flex items-center justify-center py-6">
          <Spinner class="text-n-blue-11" />
        </div>
        <AgentSelector
          v-else
          :agent-list="agentList"
          :selected-agents="selectedAgents"
          :update-selected-agents="updateSelectedAgents"
          :is-working="isCreating"
          :submit-button-text="$t('TEAMS_SETTINGS.ADD.BUTTON_TEXT')"
        />
      </div>
    </form>
  </div>
</template>
